import React, { useContext, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Tooltip } from '@eposnow/ui-core';
import { UIContext } from '../../context/UIContext';
import { DataGridContext } from '../../context/DataGridContext';

const ActionsMenu = ({
  rowId,
  showDropdownMenu = false,
  customButtons = [],
  editAction,
  isInlineEdit = false,
}: {
  rowId: string;
  showDropdownMenu?: boolean;
  customButtons?: { id: string; label: string; action: () => void; icon?: React.ReactNode }[];
  editAction?: () => void;
  isInlineEdit?: boolean;
}) => {
  const { t } = useTranslation();
  const { colorMode } = useContext(UIContext);
  const { startEditObject, objectIdBeingDeleted, deleteObject, isLoadingDelete, handleRowEdit } =
    useContext(DataGridContext);
  const theme = useTheme();
  const isDeleting = objectIdBeingDeleted === rowId && isLoadingDelete;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  let deleteIcon;

  if (isDeleting) {
    deleteIcon = <CircularProgress color="inherit" size={32} />;
  } else {
    deleteIcon = <DeleteOutlineOutlinedIcon fontSize="medium" />;
  }

  if (isInlineEdit) {
    return (
      <Box display="flex" gap={0.5} sx={{ marginRight: '8px' }}>
        <Tooltip title={t('components.gridActionsMenu.save')} theme={theme}>
          <IconButton sx={{ color: 'text.secondary' }} onClick={() => handleRowEdit(rowId, true)}>
            <SaveOutlinedIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('components.gridActionsMenu.cancel')} theme={theme}>
          <IconButton
            sx={{
              color: 'text.secondary',
              mr: 1,
            }}
            onClick={() => handleRowEdit(rowId, false)}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return showDropdownMenu ? (
    <>
      <IconButton
        onClick={handleMenuOpen}
        sx={{
          color: 'text.secondary',
          ':hover': {
            color: 'text.primary',
            backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
          },
          mr: 2,
        }}
      >
        <MoreVertIcon fontSize="medium" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (editAction) editAction();
            else if (startEditObject) startEditObject(rowId);
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('components.gridActionsMenu.edit')} />
        </MenuItem>
        {deleteObject && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              deleteObject(rowId);
            }}
          >
            <ListItemIcon>{deleteIcon}</ListItemIcon>
            <ListItemText primary={t('components.gridActionsMenu.delete')} />
          </MenuItem>
        )}
        {customButtons?.map((button) => (
          <MenuItem
            key={button.id}
            onClick={() => {
              handleMenuClose();
              button.action();
            }}
          >
            <ListItemIcon>{button.icon}</ListItemIcon>
            <ListItemText primary={button.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : (
    <Box display="flex">
      <Tooltip title={t('components.gridActionsMenu.edit')} theme={theme}>
        <IconButton
          sx={{
            color: 'text.secondary',
            ':hover': {
              color: 'text.primary',
              backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            },
          }}
          aria-label={t('components.gridActionsMenu.edit')}
          onClick={() => startEditObject(rowId)}
        >
          <EditOutlinedIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('components.gridActionsMenu.delete')} theme={theme}>
        <IconButton
          sx={{
            color: 'text.secondary',
            ':hover': {
              color: 'text.primary',
              backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            },
          }}
          aria-label={t('components.gridActionsMenu.delete')}
          onClick={() => deleteObject(rowId)}
        >
          {deleteIcon}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ActionsMenu;
